import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { currentDateTime, wtFormatDate } from 'src/helpers/commons/moment'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney } from 'src/helpers/commons/utils'
import { TYPE_TOTAL } from 'src/models/pnlConfig'

const user = wtGetFromLocalStorage('authUser')
const formatReportValue = (value) => {
  const numericValue = Number(value || 0)
  const formattedValue = wtFormatMoney(Math.abs(numericValue), '', true, 0).replace(/\s+/g, '')

  return numericValue < 0 ? `(${formattedValue})` : formattedValue
}

const Pdf = async (stockAdjustmentReportData, asOf, lastYearLastDate, branchName = '') => {
  const generatePdf = (reportData, reportAsOf, reportLastYearDate, reportBranchName) => {
    if (!reportData || reportData.length === 0) {
      lzNotifyErrorMessageFromResult('No Data found!')
      return
    }

    const doc = new jsPDF()
    const totalPagesExp = '{total_pages_count_string}'
    const logo = 'assets/wil-icon.png'

    const header = (title) => {
      const pageSize = doc.internal.pageSize
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

      doc.setFontSize(11)
      doc.setTextColor('#3c4b64')
      doc.setFont('Helvetica', 'bold')
      doc.addImage(logo, 'PNG', 5, 4, 25, 25)
      doc.line(32, 6, 32, 25)

      doc.text('WAH', 35, 10, { align: 'left' })
      doc.text('INDUSTRIES', 35, 15)
      doc.text('LIMITED', 35, 20)
      doc.setFontSize(9)
      doc.text('www.wahindustries.com', 35, 25)
      doc.setFontSize(11)
      doc.text(title, pageWidth - 90, 15, { align: 'left' })
      doc.text('AS OF ' + wtFormatDate(reportAsOf, 'MMMM D, Y'), pageWidth - 90, 22, {
        align: 'left',
      })
      doc.setFontSize(11)
      doc.setTextColor('black')
      doc.setFont('Helvetica', 'bold')
      doc.text('Branch: ', pageWidth - 90, 28, { align: 'left' })
      doc.setTextColor('#0b3d91')
      doc.text(reportBranchName || 'All', pageWidth - 72, 28, { align: 'left' })

      doc.line(10, 33, pageWidth - 10, 33)
    }

    const footer = () => {
      const pageNo = doc.internal.getNumberOfPages()
      const pageSize = doc.internal.pageSize
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()

      doc.setFontSize(10)
      doc.setTextColor('black')
      doc.setFont('Helvetica', 'normal')
      doc.text('This report is printed through ERP at ', 60, pageHeight - 15)

      doc.setTextColor('#0202f7')
      doc.setFont('Helvetica', 'bold')
      doc.text(currentDateTime(), 124, pageHeight - 15)

      let str = 'Page ' + pageNo
      if (typeof doc.putTotalPages === 'function') {
        str = str + ' of ' + totalPagesExp
      }

      doc.setTextColor('black')
      doc.setFontSize(9)
      doc.line(14, 286, 196, 286)
      doc.text(str, 230, 290, { align: 'right' })
      doc.text('Printed By ', 20, 290, { align: 'left' })
      doc.setTextColor('blue')
      doc.text(`${user ? user.name : ''}`, 37, 290, { align: 'left' })
    }

    const headRows = () => [
      [
        { content: '', styles: { halign: 'left', cellWidth: 90 } },
        { content: 'Note', styles: { halign: 'center', cellWidth: 15 } },
        {
          content: wtFormatDate(reportAsOf, 'YYYY') + '\n(Rupees)',
          styles: { halign: 'center', cellWidth: 40 },
        },
        {
          content: wtFormatDate(reportLastYearDate, 'YYYY') + '\n(Rupees)',
          styles: { halign: 'center', cellWidth: 40 },
        },
      ],
    ]

    const firstLevelData = (data) => {
      let noteValue = 0

      return data.map((row) => ({
        name: row.name,
        note: ++noteValue,
        current_year_balance: row.current_year_balance,
        last_year_balance: row.last_year_balance,
        is_heading: row.is_heading,
        type: row.type,
        hasItems: row.hasItems,
        children: Array.isArray(row.children) ? row.children : [],
      }))
    }

    const firstLevelRows = (data) =>
      firstLevelData(data).map((row) => [
        { content: row.name, styles: { halign: 'left' } },
        { content: row.note, styles: { halign: 'center' } },
        {
          content: row.is_heading === 1 ? '' : formatReportValue(row.current_year_balance),
          styles: { halign: 'right' },
        },
        {
          content: row.is_heading === 1 ? '' : formatReportValue(row.last_year_balance),
          styles: { halign: 'right' },
        },
        {
          content: row.type,
          styles: { display: 'none' },
        },
      ])

    const createNotesData = (row, depth = 0, parentNote = '') => {
      let notesRows = []
      let childNoteValue = 0
      const children = Array.isArray(row.children) ? row.children : []

      notesRows.push({
        name: `${row.note}  ${row.name}`.trim(),
        note: '',
        current_year_balance: '',
        last_year_balance: '',
        type: row.type,
        rowToBold: true,
        rowToUnderline: false,
      })

      children.forEach((child) => {
        const childNote = parentNote || row.note
        const fullNote = `${childNote}.${++childNoteValue}`
        const indent = '    '.repeat(depth + 1)

        notesRows.push({
          name: indent + child.name,
          note: fullNote,
          current_year_balance: child.current_year_balance,
          last_year_balance: child.last_year_balance,
          type: child.type,
          rowToBold: child.type === TYPE_TOTAL || child.is_heading === 1 || child.hasItems,
          rowToUnderline: child.type === TYPE_TOTAL,
        })

        if (Array.isArray(child.children) && child.children.length > 0) {
          notesRows = [
            ...notesRows,
            ...createNestedNotesData(child.children, depth + 1, fullNote),
          ]
        }
      })

      return notesRows
    }

    const createNestedNotesData = (rows = [], depth = 0, parentNote = '') => {
      let notesRows = []
      let childNoteValue = 0

      rows.forEach((row) => {
        const fullNote = `${parentNote}.${++childNoteValue}`
        const indent = '    '.repeat(depth + 1)

        notesRows.push({
          name: indent + row.name,
          note: fullNote,
          current_year_balance: row.current_year_balance,
          last_year_balance: row.last_year_balance,
          type: row.type,
          rowToBold: row.type === TYPE_TOTAL || row.is_heading === 1 || row.hasItems,
          rowToUnderline: row.type === TYPE_TOTAL,
        })

        if (Array.isArray(row.children) && row.children.length > 0) {
          notesRows = [...notesRows, ...createNestedNotesData(row.children, depth + 1, fullNote)]
        }
      })

      return notesRows
    }

    const detailRows = (data) =>
      firstLevelData(data)
        .flatMap((row) => createNotesData(row))
        .map((row) => [
        {
          content: row.name,
          styles: {
            halign: 'left',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
            fontSize: row.rowToBold ? 10 : 9,
          },
        },
        {
          content: row.note,
          styles: {
            halign: 'center',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
            fontSize: row.rowToBold ? 10 : 9,
          },
        },
        {
          content:
            row.current_year_balance === ''
              ? ''
              : formatReportValue(row.current_year_balance),
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
          },
        },
        {
          content:
            row.last_year_balance === ''
              ? ''
              : formatReportValue(row.last_year_balance),
          styles: {
            halign: 'right',
            fontStyle: row.rowToBold ? 'bold' : 'normal',
          },
        },
        { content: row.type, styles: { display: 'none' } },
        { content: row.rowToUnderline, styles: { display: 'none' } },
      ])

    const drawSummaryPage = () => {
      doc.autoTable({
        head: headRows(),
        body: firstLevelRows(reportData),
        theme: 'plain',
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [60, 75, 100],
          fontSize: 10,
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: 90, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
          1: { halign: 'center', cellWidth: 15, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
          2: { halign: 'right', cellWidth: 40, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
          3: { halign: 'right', cellWidth: 40, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
        },
        margin: { top: 43, bottom: 20, left: 10 },
        didDrawPage: () => {
          header('STOCK ADJUSTMENT REPORT')
          footer()
        },
        willDrawCell: (data) => {
          if (data.row.section === 'head' && data.column.index > 1) {
            const { cell } = data
            doc.setDrawColor(0, 0, 0)
            doc.setLineWidth(1)
            doc.line(cell.x + 2, cell.y + cell.height, cell.x + cell.width - 2, cell.y + cell.height)
          }

          if (data.row.section === 'body' && data.row.raw[4].content === 2 && data.column.index > 1) {
            const { cell } = data
            doc.setDrawColor(0, 0, 0)
            doc.setLineWidth(0.2)
            doc.line(cell.x + 2, cell.y, cell.x + cell.width - 2, cell.y)
          }
        },
        didParseCell: (data) => {
          if (data.section === 'head' && data.column.index === 2) {
            data.cell.styles.textColor = [14, 116, 144]
          }
          if (data.section === 'head' && data.column.index === 3) {
            data.cell.styles.textColor = [166, 76, 29]
          }
          if (data.section === 'body' && data.row.raw[4].content === 2) {
            data.cell.styles.fontSize = 10
            data.cell.styles.fontStyle = 'bold'
          }
          if (data.section === 'body' && data.column.index === 2) {
            data.cell.styles.textColor = [14, 116, 144]
          }
          if (data.section === 'body' && data.column.index === 3) {
            data.cell.styles.textColor = [166, 76, 29]
          }
          if (data.section === 'head') {
            data.cell.styles.fontStyle = 'bold'
          }
        },
      })
    }

    const drawDetailPage = () => {
      doc.addPage()
      doc.autoTable({
        head: headRows(),
        body: detailRows(reportData),
        theme: 'plain',
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [60, 75, 100],
          fontSize: 10,
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: 90, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
          1: { halign: 'center', cellWidth: 15, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
          2: { halign: 'right', cellWidth: 40, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
          3: { halign: 'right', cellWidth: 40, minCellHeight: 6, font: 'helvetica', fontSize: 9 },
        },
        margin: { top: 43, bottom: 20, left: 10 },
        didDrawPage: () => {
          header('STOCK ADJUSTMENT REPORT DETAIL')
          footer()
        },
        willDrawCell: (data) => {
          if (data.row.section === 'head' && data.column.index > 1) {
            const { cell } = data
            doc.setDrawColor(0, 0, 0)
            doc.setLineWidth(1)
            doc.line(cell.x + 2, cell.y + cell.height, cell.x + cell.width - 2, cell.y + cell.height)
          }

          if (data.row.section === 'body' && data.row.raw[5].content === true && data.column.index > 1) {
            const { cell } = data
            doc.setDrawColor(0, 0, 0)
            doc.setLineWidth(0.5)
            doc.line(cell.x + 2, cell.y, cell.x + cell.width - 2, cell.y)
            doc.line(cell.x + 2, cell.y + 6, cell.x + cell.width - 2, cell.y + 6)
          }
        },
        didParseCell: (data) => {
          if (data.section === 'head' && data.column.index === 2) {
            data.cell.styles.textColor = [14, 116, 144]
          }
          if (data.section === 'head' && data.column.index === 3) {
            data.cell.styles.textColor = [166, 76, 29]
          }
          if (data.section === 'body' && data.column.index === 2) {
            data.cell.styles.textColor = [14, 116, 144]
          }
          if (data.section === 'body' && data.column.index === 3) {
            data.cell.styles.textColor = [166, 76, 29]
          }
          if (data.section === 'body' && data.row.raw[4].content === 2) {
            data.cell.styles.fontSize = 10.5
            data.cell.styles.fontStyle = 'bold'
          }
          if (data.section === 'head') {
            data.cell.styles.fontStyle = 'bold'
          }
        },
      })
    }

    drawSummaryPage()
    drawDetailPage()

    if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages(totalPagesExp)
    }

    const pdfBlob = doc.output('blob')
    const pdfUrl = URL.createObjectURL(pdfBlob)
    window.open(pdfUrl, '_blank')
  }

  generatePdf(stockAdjustmentReportData, asOf, lastYearLastDate, branchName)
}

export default Pdf
